import { useMemo } from "react";
import dayjs from "dayjs";
import { parseDataKey } from "../utils/dateRange";

/* =========================
   TOTAL VIEWERS CALCULATION
========================= */

function generateTotalNumberFromTime() {
  const outputTotalValues = [];
  for (let minutesSinceMidnight = 0; minutesSinceMidnight < 1440; minutesSinceMidnight++) {
    const phaseAngle = (2 * Math.PI * minutesSinceMidnight) / 1440;
    let outputTotalValue = (3.14 + Math.cos((phaseAngle + Math.PI / 2)) * 0.5) * 5.5 * 0.95;
    const noise = Math.random() * 0.02 * 2 - 0.02;
    outputTotalValue += noise;
    outputTotalValues.push(outputTotalValue);
  }
  return outputTotalValues;
}

const viewerTotalNumbers = generateTotalNumberFromTime();

function fillZeroValues(data) {
  if (!data) return data;
  
  const result = {};
  
  Object.entries(data).forEach(([tempDate, tempDay]) => {
    const filledDay = [...tempDay];
    
    // Fill zeros by averaging adjacent values
    for (let j = 1; j < filledDay.length - 1; j++) {
      if (filledDay[j] === 0) {
        filledDay[j] = Math.floor((filledDay[j - 1] + filledDay[j + 1]) / 2);
      }
    }
    
    result[tempDate] = filledDay;
  });
  
  return result;
}

function applyTotalViewersMultiplier(rawImpressions) {
  if (!rawImpressions) return {};
  
  // First fill zero values
  const filledData = fillZeroValues(rawImpressions);
  
  // Then apply multiplier
  const tempVideoImpressionTotal = {};
  
  Object.entries(filledData).forEach(([tempDate, consumption]) => {
    tempVideoImpressionTotal[tempDate] = consumption.map((value, j) => {
      if (j < viewerTotalNumbers.length) {
        return value * viewerTotalNumbers[j];
      }
      return value;
    });
  });
  
  return tempVideoImpressionTotal;
}

/* =========================
   HELPERS
========================= */

function uiDateToDataDay(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  const dt = dayjs(`${d}-${m}-${y}`, "DD-MM-YYYY", true);
  return dt.isValid() ? dt : null;
}

function getWeekStartMonday(d) {
  const offset = (d.day() + 6) % 7;
  return d.subtract(offset, "day").startOf("day");
}

/* =========================
   MAIN HOOK
========================= */

export function useTotalViewersSeries({
  rawImpressions,
  sortedImpKeys,
  startDateInput,
  endDateInput,
  startTime,
  endTime,
  mode,
}) {
  return useMemo(() => {
    if (!rawImpressions || !sortedImpKeys?.length) return [];

    // Apply total viewers transformation
    const totalViewersData = applyTotalViewersMultiplier(rawImpressions);

    /* =========================
       MINUTE MODE
    ========================= */
    if (mode === "minute") {
      const uiKey = startDateInput
        ? startDateInput.split("-").reverse().join("-")
        : sortedImpKeys.at(-1);

      const arr = Array.isArray(totalViewersData[uiKey])
        ? totalViewersData[uiKey]
        : [];

      const toMinute = (t) => {
        const [h, m] = t.split(":").map(Number);
        return h * 60 + m;
      };

      const startMinute = toMinute(startTime);
      const endMinute = toMinute(endTime);
      if (endMinute < startMinute) return [];

      const out = [];
      for (let i = startMinute; i <= endMinute; i++) {
        const hh = String(Math.floor(i / 60)).padStart(2, "0");
        const mm = String(i % 60).padStart(2, "0");
        out.push({
          index: i, // minute of the day — the chart's clock axis
          label: `${hh}:${mm}`,
          value: Math.round(Number(arr[i]) || 0),
        });
      }

      return out;
    }

    /* =========================
       DATE RANGE (OTHER MODES)
    ========================= */

    const start = uiDateToDataDay(startDateInput);
    const end = uiDateToDataDay(endDateInput);
    if (!start || !end) return [];

    /* =========================
       HOUR MODE
    ========================= */
    if (mode === "hour") {
      const out = [];

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const arr = totalViewersData[key] || [];

        for (let h = 0; h < 24; h++) {
          const slice = arr.slice(h * 60, (h + 1) * 60);
          const sum = slice.reduce((acc, val) => acc + (Number(val) || 0), 0);

          out.push({
            label: `${d.format("DD/MM")} ${String(h).padStart(2, "0")}:00`,
            value: Math.round(sum),
          });
        }
      });

      return out;
    }

    /* =========================
       DAY MODE
    ========================= */
    if (mode === "day") {
      const out = [];

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const arr = totalViewersData[key] || [];
        const sum = arr.reduce((acc, val) => acc + (Number(val) || 0), 0);

        out.push({
          label: d.format("DD/MM/YYYY"),
          value: Math.round(sum),
        });
      });

      return out;
    }

    /* =========================
       WEEK MODE
    ========================= */
    if (mode === "week") {
      const bucket = new Map();

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const weekStart = getWeekStartMonday(d);
        const bucketKey = weekStart.format("YYYY-MM-DD");

        const arr = totalViewersData[key] || [];
        const sum = arr.reduce((a, b) => a + (Number(b) || 0), 0);

        bucket.set(bucketKey, (bucket.get(bucketKey) || 0) + sum);
      });

      return Array.from(bucket.entries()).map(([key, sum]) => ({
        label: dayjs(key).format("DD.MM"),
        value: Math.round(sum),
      }));
    }

    /* =========================
       MONTH MODE
    ========================= */
    if (mode === "month") {
      const bucket = new Map();

      sortedImpKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const bucketKey = d.format("YYYY-MM");

        const arr = totalViewersData[key] || [];
        const sum = arr.reduce((a, b) => a + (Number(b) || 0), 0);

        bucket.set(bucketKey, (bucket.get(bucketKey) || 0) + sum);
      });

      return Array.from(bucket.entries()).map(([key, sum]) => ({
        label: dayjs(key).format("MM.YYYY"),
        value: Math.round(sum),
      }));
    }

    return [];
  }, [rawImpressions, sortedImpKeys, startDateInput, endDateInput, startTime, endTime, mode]);
}
